import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { Trash2, AlertTriangle, Download, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AccountDeletionButton = ({ variant = "outline", className = "" }) => {
  const { token, logout } = useAuth();
  const { t } = useLanguage();
  
  const [showDialog, setShowDialog] = useState(false);
  const [step, setStep] = useState(1); // 1: Info, 2: Export, 3: Confirm
  const [confirmText, setConfirmText] = useState('');
  const [understood, setUnderstood] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/api/gdpr/export`, { headers });
      
      // Create and download JSON file
      const dataStr = JSON.stringify(response.data.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mes_donnees_drbooking_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Vos données ont été téléchargées avec succès');
      setStep(3);
    } catch (error) {
      toast.error('Erreur lors de l\'export des données');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (confirmText.toLowerCase() !== 'supprimer' || !understood) {
      toast.error('Veuillez confirmer correctement la suppression');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/gdpr/delete-account`, {}, { headers });
      
      toast.success('Votre compte a été supprimé avec succès');
      setShowDialog(false);
      
      // Logout and redirect
      setTimeout(() => {
        logout();
        window.location.href = '/';
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression du compte');
    } finally {
      setLoading(false);
    }
  };

  const resetDialog = () => {
    setStep(1);
    setConfirmText('');
    setUnderstood(false);
  };

  const handleOpenChange = (open) => {
    setShowDialog(open);
    if (!open) {
      resetDialog();
    }
  };

  return (
    <>
      <Button 
        variant={variant}
        className={`gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 ${className}`}
        onClick={() => setShowDialog(true)}
        data-testid="delete-account-btn"
      >
        <Trash2 className="w-4 h-4" />
        Désinscription
      </Button>

      <Dialog open={showDialog} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Suppression de compte
            </DialogTitle>
            <DialogDescription>
              {step === 1 && "Cette action est irréversible. Toutes vos données seront définitivement supprimées."}
              {step === 2 && "Souhaitez-vous télécharger vos données avant la suppression ?"}
              {step === 3 && "Veuillez confirmer la suppression de votre compte."}
            </DialogDescription>
          </DialogHeader>

          {/* Step 1: Information */}
          {step === 1 && (
            <div className="py-4 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-semibold text-amber-800 mb-2">Ce qui sera supprimé :</h4>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• Votre profil et informations personnelles</li>
                  <li>• Votre historique de rendez-vous</li>
                  <li>• Vos préférences et favoris</li>
                  <li>• Vos messages et notifications</li>
                  <li>• Vos points de fidélité</li>
                </ul>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">Droit RGPD :</h4>
                <p className="text-sm text-blue-700">
                  Conformément au RGPD, vous pouvez exporter vos données avant la suppression. 
                  Cette démarche est gratuite et vous permet de conserver une copie de vos informations.
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Export Option */}
          {step === 2 && (
            <div className="py-4 space-y-4">
              <div className="flex flex-col items-center justify-center py-6 bg-slate-50 rounded-lg">
                <Download className="w-12 h-12 text-blue-500 mb-4" />
                <p className="text-center text-slate-600 mb-4">
                  Téléchargez une copie de toutes vos données personnelles au format JSON.
                </p>
                <Button 
                  onClick={handleExportData} 
                  disabled={exporting}
                  className="gap-2"
                  variant="outline"
                >
                  {exporting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Export en cours...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Télécharger mes données
                    </>
                  )}
                </Button>
              </div>
              
              <p className="text-center text-sm text-slate-500">
                Ou passez directement à la suppression
              </p>
            </div>
          )}

          {/* Step 3: Final Confirmation */}
          {step === 3 && (
            <div className="py-4 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 font-medium text-center">
                  Cette action est IRRÉVERSIBLE
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirm-text">
                  Tapez <span className="font-bold text-red-600">SUPPRIMER</span> pour confirmer :
                </Label>
                <Input
                  id="confirm-text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="Tapez SUPPRIMER"
                  data-testid="confirm-delete-input"
                />
              </div>
              
              <div className="flex items-start gap-3">
                <Checkbox
                  id="understood"
                  checked={understood}
                  onCheckedChange={setUnderstood}
                  data-testid="confirm-delete-checkbox"
                />
                <Label htmlFor="understood" className="text-sm leading-relaxed cursor-pointer">
                  Je comprends que toutes mes données seront définitivement supprimées et que cette action est irréversible.
                </Label>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            {step === 1 && (
              <>
                <Button variant="outline" onClick={() => handleOpenChange(false)}>
                  Annuler
                </Button>
                <Button onClick={() => setStep(2)} className="bg-amber-600 hover:bg-amber-700">
                  Continuer
                </Button>
              </>
            )}
            
            {step === 2 && (
              <>
                <Button variant="outline" onClick={() => setStep(1)}>
                  Retour
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={() => setStep(3)}
                >
                  Passer à la suppression
                </Button>
              </>
            )}
            
            {step === 3 && (
              <>
                <Button variant="outline" onClick={() => setStep(2)}>
                  Retour
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleDeleteAccount}
                  disabled={loading || confirmText.toLowerCase() !== 'supprimer' || !understood}
                  data-testid="confirm-delete-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Suppression...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Supprimer définitivement
                    </>
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AccountDeletionButton;
